import React from "react";
import {
  Container,
  Grid,
  Typography,
  Paper,
  Box,
  useTheme,
  alpha,
  useMediaQuery,
  Card,
  CardContent,
  Avatar,
  Button,
  Chip,
  Divider,
  Stack,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stepper,
  Step,
  StepLabel,
  StepContent,
} from "@mui/material";
import {
  Receipt as EInvoiceIcon,
  Dashboard as DashboardIcon,
  CheckCircle as CheckIcon,
  AutoAwesome as PremiumIcon,
  Download as DownloadIcon,
  Email as EmailIcon,
  Share as ShareIcon,
  Security as SecurityIcon,
  PictureAsPdf as PdfIcon,
  Analytics as AnalyticsIcon,
  Schedule as ScheduleIcon,
  Groups as GroupsIcon,
  Payment as PaymentIcon,
  CloudSync as SyncIcon,
  Smartphone as MobileIcon,
  Verified as VerifiedIcon,
  Lock as LockIcon,
  Speed as SpeedIcon,
  IntegrationInstructions as IntegrationIcon,
} from "@mui/icons-material";
import { styled } from "@mui/material/styles";

// Import your images
import einvoiceDashboard from "./Images/E-Invoice.png";
import einvoiceCreate from "./Images/StepOfInvoice.png";
import einvoiceIRN from "./Images/InrGeneration.png";
import einvoiceGST from "./Images/einvoiceGST.png";
import einvoiceMobile from "./Images/accounting.png";

const EInvoicing = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const PremiumCard = styled(Card)(({ theme }) => ({
    background: `linear-gradient(135deg, ${alpha(
      theme.palette.primary.main,
      0.05
    )} 0%, ${alpha(theme.palette.success.main, 0.05)} 100%)`,
    border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
    borderRadius: 20,
    padding: theme.spacing(3),
    position: "relative",
    overflow: "hidden",
    "&::before": {
      content: '""',
      position: "absolute",
      top: 0,
      left: 0,
      right: 0,
      height: 4,
      background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.success.main})`,
    },
  }));

  const features = [
    {
      title: "IRN Generation",
      description: "Instant IRN generation with real-time GSTIN validation",
      icon: <VerifiedIcon sx={{ fontSize: 32 }} />,
      color: theme.palette.success.main,
    },
    {
      title: "QR Code & Digital Sign",
      description: "Auto-generated QR codes with digital signatures",
      icon: <LockIcon sx={{ fontSize: 32 }} />,
      color: theme.palette.primary.main,
    },
    {
      title: "GSTR-1 Auto-Fill",
      description: "Automatic GSTR-1 preparation from e-invoices",
      icon: <SyncIcon sx={{ fontSize: 32 }} />,
      color: theme.palette.info.main,
    },
    {
      title: "Bulk Processing",
      description: "Generate multiple e-invoices in one click",
      icon: <SpeedIcon sx={{ fontSize: 32 }} />,
      color: theme.palette.warning.main,
    },
    {
      title: "API Integration",
      description: "Seamless integration with your existing systems",
      icon: <IntegrationIcon sx={{ fontSize: 32 }} />,
      color: theme.palette.error.main,
    },
    {
      title: "Real-time Tracking",
      description: "Live status tracking of all e-invoice submissions",
      icon: <AnalyticsIcon sx={{ fontSize: 32 }} />,
      color: theme.palette.secondary.main,
    },
  ];

  const complianceFeatures = [
    {
      title: "GSTIN Validation",
      description: "Real-time GSTIN verification before IRN generation",
    },
    {
      title: "HSN/SAC Automation",
      description: "Smart HSN code suggestions and validation",
    },
    {
      title: "E-way Bill Integration",
      description: "Auto e-way bill generation with Part-A details",
    },
    {
      title: "Legal Compliance",
      description: "100% compliant with latest GST council guidelines",
    },
  ];

  const steps = [
    {
      label: "Create Invoice",
      description: "Fill invoice details with auto-validated fields",
    },
    {
      label: "IRN Generation",
      description: "Instant IRN generation from GSTN portal",
    },
    {
      label: "QR Code & Signing",
      description: "Digital signing and QR code generation",
    },
    {
      label: "Delivery & Reporting",
      description: "Share with customer and auto-update GSTR-1",
    },
  ];

  return (
    <Box
      sx={{
        pb: 8,
        background: "linear-gradient(135deg, #f8faff 0%, #f0fff4 100%)",
      }}
    >
      {/* Hero Section */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${alpha(
            theme.palette.primary.main,
            0.08
          )} 0%, ${alpha(theme.palette.success.main, 0.08)} 100%)`,
          py: { xs: 8, md: 12 },
          position: "relative",
          overflow: "hidden",
        }}
      >
        <Container maxWidth="lg">
          <Grid
            container
            spacing={6}
            alignItems="center"
            direction={{ xs: "column-reverse", md: "row" }}
          >
            {/* Left Content */}
            <Grid size={{ xs: 12, md: 6 }}>
              <Chip
                icon={<VerifiedIcon />}
                label="GSTN Certified E-Invoicing"
                color="success"
                variant="filled"
                sx={{
                  mb: 3,
                  fontWeight: 600,
                  background: `linear-gradient(135deg, ${theme.palette.success.main} 0%, ${theme.palette.primary.main} 100%)`,
                  fontSize: "0.9rem",
                }}
              />

              <Typography
                variant="h1"
                sx={{
                  fontWeight: 800,
                  mb: 3,
                  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.success.main} 100%)`,
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  fontSize: { xs: "2.5rem", md: "3.5rem" },
                  lineHeight: 1.1,
                }}
              >
                GST Compliant <br /> E-Invoicing Made Simple
              </Typography>

              <Typography
                variant="h5"
                color="text.secondary"
                sx={{ mb: 4, lineHeight: 1.6, fontWeight: 400 }}
              >
                Generate IRN, sign digitally, and create QR codes automatically.
                100% compliant with GST e-invoicing regulations. Perfect for
                businesses above ₹20Cr turnover.
              </Typography>

              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={2}
                sx={{ mb: 4 }}
              >
                <Button
                  variant="contained"
                  size="large"
                  sx={{
                    px: 4,
                    py: 1.5,
                    fontWeight: 700,
                    borderRadius: 3,
                    background: `linear-gradient(135deg, ${theme.palette.success.main} 0%, ${theme.palette.primary.main} 100%)`,
                    boxShadow: "0 8px 25px rgba(76, 175, 80, 0.3)",
                    "&:hover": {
                      boxShadow: "0 12px 35px rgba(76, 175, 80, 0.4)",
                    },
                  }}
                  startIcon={<EInvoiceIcon />}
                >
                  Generate E-Invoice
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  sx={{
                    px: 4,
                    py: 1.5,
                    fontWeight: 600,
                    borderRadius: 3,
                    borderWidth: 2,
                    borderColor: theme.palette.primary.main,
                    "&:hover": {
                      borderWidth: 2,
                    },
                  }}
                >
                  View Compliance Guide
                </Button>
              </Stack>

              {/* Trust Indicators */}
              <Stack direction="row" spacing={4} sx={{ mt: 4 }}>
                <Box>
                  <Typography
                    variant="h4"
                    fontWeight={800}
                    color="success.main"
                  >
                    10L+
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 0.5 }}
                  >
                    E-Invoices Generated
                  </Typography>
                </Box>
                <Box>
                  <Typography
                    variant="h4"
                    fontWeight={800}
                    color="success.main"
                  >
                    99.9%
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 0.5 }}
                  >
                    Success Rate
                  </Typography>
                </Box>
                <Box>
                  <Typography
                    variant="h4"
                    fontWeight={800}
                    color="success.main"
                  >
                    <VerifiedIcon
                      sx={{
                        fontSize: "inherit",
                        verticalAlign: "middle",
                        mr: 0.2,
                        mb: 1,
                      }}
                    />
                    GSTN
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Certified
                  </Typography>
                </Box>
              </Stack>
            </Grid>

            {/* Right Content - Hero Image */}
            <Grid size={{ xs: 12, md: 6 }}>
              <Box
                sx={{
                  position: "relative",
                  "&::before": {
                    content: '""',
                    position: "absolute",
                    top: -20,
                    right: -20,
                    bottom: -20,
                    left: -20,
                    background: `linear-gradient(135deg, ${alpha(
                      theme.palette.success.main,
                      0.1
                    )} 0%, transparent 50%)`,
                    borderRadius: 30,
                    zIndex: 0,
                  },
                }}
              >
                <Box
                  component="img"
                  src={einvoiceDashboard}
                  alt="E-Invoicing Dashboard"
                  sx={{
                    width: "100%",
                    height: "auto",
                    borderRadius: 4,
                    boxShadow: "0 25px 50px rgba(0, 0, 0, 0.15)",
                    position: "relative",
                    zIndex: 1,
                    transform:
                      "perspective(1000px) rotateY(-5deg) rotateX(5deg)",
                    transition: "transform 0.3s ease",
                    "&:hover": {
                      transform:
                        "perspective(1000px) rotateY(0deg) rotateX(0deg)",
                    },
                  }}
                />
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Features Grid */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Box textAlign="center" sx={{ mb: 6 }}>
          <Typography variant="h3" fontWeight={700} sx={{ mb: 1 }}>
            Complete E-Invoicing
            <Box component="span" color="success.main">
              {" "}
              Solution
            </Box>
          </Typography>
          <Typography
            variant="h6"
            color="text.secondary"
            sx={{ maxWidth: 600, mx: "auto" }}
          >
            Everything you need for GST-compliant e-invoicing in one platform
          </Typography>
        </Box>

        <Grid container spacing={3}>
          {features.map((feature, index) => (
            <Grid size={{ xs: 12, sm: 6, md: 4 }} key={index}>
              <PremiumCard>
                <CardContent sx={{ textAlign: "center", p: 3 }}>
                  <Avatar
                    sx={{
                      bgcolor: alpha(feature.color, 0.1),
                      color: feature.color,
                      width: 70,
                      height: 70,
                      mb: 3,
                      mx: "auto",
                      border: `2px solid ${alpha(feature.color, 0.2)}`,
                    }}
                  >
                    {feature.icon}
                  </Avatar>
                  <Typography variant="h5" fontWeight={700} sx={{ mb: 1 }}>
                    {feature.title}
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    {feature.description}
                  </Typography>
                </CardContent>
              </PremiumCard>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* E-Invoicing Process */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Typography
          variant="h3"
          fontWeight={700}
          sx={{ mb: 1, textAlign: "center" }}
        >
          Simple <span style={{ color: "#2e7d32" }}>4-Step</span> E-Invoicing
          Process
        </Typography>
        <Typography
          variant="h6"
          color="text.secondary"
          sx={{ mb: 1, textAlign: "center" }}
        >
          From invoice creation to GST reporting - completely automated and
          compliant
        </Typography>
        <Grid container spacing={6} alignItems="center">
          <Grid size={{ xs: 12, md: 6 }}>
            <Stepper orientation="vertical" sx={{ mt: 2 }}>
              {steps.map((step, index) => (
                <Step key={step.label} active={true} completed={true}>
                  <StepLabel
                    StepIconComponent={() => (
                      <Avatar
                        sx={{ bgcolor: "success.main", width: 32, height: 32 }}
                      >
                        {index + 1}
                      </Avatar>
                    )}
                    sx={{ "& .MuiStepLabel-label": { fontWeight: 600 } }}
                  >
                    {step.label}
                  </StepLabel>
                  <StepContent>
                    <Typography color="text.secondary">
                      {step.description}
                    </Typography>
                  </StepContent>
                </Step>
              ))}
            </Stepper>
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <Box
              component="img"
              src={einvoiceCreate}
              alt="E-Invoice Creation"
              sx={{
                width: "100%",
                height: "auto",
                borderRadius: 4,
                boxShadow: "0 20px 40px rgba(0, 0, 0, 0.1)",
              }}
            />
          </Grid>
        </Grid>
      </Container>

      {/* Compliance & Security */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Typography
          variant="h3"
          fontWeight={700}
          sx={{ mb: 1, textAlign: "center" }}
        >
          <span style={{ color: "#2E7D32" }}>IRN</span> Generation & Validation
        </Typography>
        <Typography
          variant="h6"
          color="text.secondary"
          sx={{ mb: 2, textAlign: "center" }}
        >
          Instant Invoice Reference Number (IRN) generation with complete
          validation and error handling
        </Typography>
        <Grid
          container
          spacing={6}
          alignItems="center"
          direction={{ xs: "column-reverse", md: "row" }}
        >
          <Grid size={{ xs: 12, md: 6 }}>
            <Box
              component="img"
              src={einvoiceIRN}
              alt="IRN Generation"
              sx={{
                width: "100%",
                height: "auto",
                borderRadius: 4,
                boxShadow: "0 20px 40px rgba(0, 0, 0, 0.1)",
              }}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <List sx={{ width: "100%" }}>
              {[
                "Real-time GSTIN validation with IRP",
                "Auto-population of mandatory fields",
                "Duplicate invoice prevention",
                "Error resolution with smart suggestions",
                "Bulk IRN generation for multiple invoices",
                "Instant status tracking with acknowledgement number",
              ].map((feature, index) => (
                <ListItem key={index} sx={{ px: 0, py: 1 }}>
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <CheckIcon color="success" />
                  </ListItemIcon>
                  <ListItemText
                    primary={feature}
                    primaryTypographyProps={{ variant: "h6", fontWeight: 500 }}
                  />
                </ListItem>
              ))}
            </List>
          </Grid>
        </Grid>
      </Container>

      {/* GST Compliance */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Typography
          variant="h3"
          fontWeight={700}
          sx={{ mb: 1, textAlign: "center" }}
        >
          Complete <span style={{ color: "#2E7D32" }}>GST</span> Compliance
        </Typography>
        <Typography
          variant="h6"
          color="text.secondary"
          sx={{ mb: 4, textAlign: "center" }}
        >
          Stay 100% compliant with all GST regulations and reporting
          requirements
        </Typography>
        <Grid container spacing={6} alignItems="center">
          <Grid size={{ xs: 12, md: 6 }}>
            <Grid container spacing={3}>
              {complianceFeatures.map((feature, index) => (
                <Grid size={{ xs: 12, sm: 6 }} key={index}>
                  <Card
                    sx={{
                      p: 2,
                      borderRadius: 3,
                      height: "80%",
                      border: `1px solid ${alpha(
                        theme.palette.success.main,
                        0.2
                      )}`,
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                      <SecurityIcon color="success" sx={{ mr: 1 }} />
                      <Typography variant="h6" fontWeight={600}>
                        {feature.title}
                      </Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      {feature.description}
                    </Typography>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <Box
              component="img"
              src={einvoiceGST}
              alt="GST Compliance"
              sx={{
                width: "100%",
                height: "auto",
                borderRadius: 4,
                boxShadow: "0 20px 40px rgba(0, 0, 0, 0.1)",
              }}
            />
          </Grid>
        </Grid>
      </Container>

      {/* Integration & Benefits */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <PremiumCard>
          <Grid container spacing={4}>
            <Grid>
              <Typography variant="h3" fontWeight={700} sx={{ mb: 3 }}>
                Seamless Integration with{" "}
                <span style={{ color: "#2e7d32" }}>RiteAccount</span>
              </Typography>
              <Typography variant="h6" color="text.secondary" sx={{ mb: 3 }}>
                Your e-invoices automatically sync with all accounting modules
                and GST returns
              </Typography>

              <Stack spacing={2}>
                {[
                  "Auto-population in GSTR-1 and GSTR-2B",
                  "Real-time e-way bill generation",
                  "Automatic credit reconciliation",
                  "Seamless data flow to accounting ledgers",
                  "Multi-company and multi-GSTIN support",
                  "Complete audit trail for all transactions",
                ].map((item, index) => (
                  <Box
                    key={index}
                    sx={{ display: "flex", alignItems: "flex-start" }}
                  >
                    <SyncIcon color="success" sx={{ mr: 2, mt: 0.5 }} />
                    <Typography variant="body1" fontWeight={500}>
                      {item}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            </Grid>
          </Grid>
        </PremiumCard>
      </Container>

      {/* Final CTA */}
      <Container maxWidth="lg" sx={{ py: 8, textAlign: "center" }}>
        <PremiumCard>
          <Typography variant="h3" fontWeight={700} mb={2}>
            Start Your E-Invoicing Journey Today
          </Typography>
          <Typography variant="h6" color="text.secondary" sx={{ mb: 4 }}>
            Join 50,000+ businesses using our GSTN-certified e-invoicing
            solution. Stay compliant, save time, and grow your business.
          </Typography>

          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            justifyContent="center"
          >
            <Button
              variant="contained"
              size="large"
              sx={{
                px: 5,
                py: 1.5,
                fontWeight: 700,
                borderRadius: 3,
                background: `linear-gradient(135deg, ${theme.palette.success.main} 0%, ${theme.palette.primary.main} 100%)`,
                fontSize: "1.1rem",
              }}
              startIcon={<EInvoiceIcon />}
            >
              Generate E-Invoice Now
            </Button>
            <Button
              variant="outlined"
              size="large"
              sx={{
                px: 5,
                py: 1.5,
                fontWeight: 600,
                borderRadius: 3,
                borderWidth: 2,
                borderColor: theme.palette.primary.main,
                fontSize: "1.1rem",
              }}
            >
              Book Compliance Demo
            </Button>
          </Stack>

          <Typography variant="body2" color="text.secondary" sx={{ mt: 3 }}>
            ✓ GSTN Certified • ✓ 30-day free trial • ✓ Setup in 10 minutes
          </Typography>
        </PremiumCard>
      </Container>
    </Box>
  );
};

// Add missing icon component
const TrendingUpIcon = (props) => (
  <svg {...props} fill="currentColor" viewBox="0 0 24 24">
    <path d="M16 6l2.29 2.29-4.88 4.88-4-4L2 16.59 3.41 18l6-6 4 4 6.3-6.29L22 12V6z" />
  </svg>
);

export default EInvoicing;
